import { Component } from "react";
import styled from "styled-components";
import * as PropTypes from "prop-types";
import { roundMinutesNumber } from "utils/Date";
import { Bounds } from "./PropTypes";
import moment from "moment";
import TYPES from "components/TimeSlotEditor/Types";

export default class Calendar extends Component {
  static propTypes = {
    bounds: Bounds,
    height: PropTypes.string,
    timeBlocks: PropTypes.array,
    highlights: PropTypes.array,
    dayparts: PropTypes.array,
    width: PropTypes.string,
    timeStep: PropTypes.number,
    labelOffset: PropTypes.number,
  };

  static defaultProps = {
    timeStep: 1,
    labelOffset: 42,
    height: "10rem",
    width: "100%",
    timeBlocks: [],
    highlights: [],
    dayparts: [],
  };

  calcConstants() {
    const {
      bounds: { end, start, startMinutes, endMinutes },
    } = this.props;

    // If the start and end fall at an hour exactly (minutes === 0)
    // add an extra hour line, because that line will not be provided in highlights.
    let extra = 1;
    if (startMinutes > 0 || endMinutes > 0) {
      extra = 0;
    }
    // eslint-disable-next-line
    const count = (end - start) / this.props.timeStep + extra;
    const endAdjusted = end - start;
    const offset = 0;

    return { count, endAdjusted, offset };
  }

  /**
   * Calculate the offset from the top in percentages based on time in hours.
   * the time in hours can contain minutes by making it a decimal number.
   * I.E. 8.5 hours = 8:30
   */
  calcTop(hour, constants) {
    return ((hour - this.props.bounds.start) / constants.endAdjusted) * 100;
  }

  hourLine(hour, constants) {
    const top = this.calcTop(hour, constants);

    return (
      <HourLine key={`hourLine${top}`} top={top}>
        <Label labelOffset={this.props.labelOffset}>
          {this.buildDisplayTime(hour)}
        </Label>
      </HourLine>
    );
  }

  renderHighlights(constants) {
    const rendered = [];
    for (const highlight of this.props.highlights) {
      rendered.push(this.hourLine(highlight.start, constants));
    }

    return rendered;
  }

  renderHourLines(constants) {
    const lines = [];

    // Get the start hour rounded to the nearest timeStep.
    let hour = roundMinutesNumber(this.props.bounds.start, this.props.timeStep);

    // Create hour lines
    for (let i = 0; i < constants.count; i++) {
      const hourLine = this.hourLine(hour, constants);
      if (hourLine) {
        lines.push(hourLine);
      }
      hour += this.props.timeStep;
    }

    return lines;
  }

  buildDisplayTime(time) {
    let hours = Math.floor(time);
    let minutes = Math.round((time - hours) * 60);
    if (minutes < 10) {
      minutes = `0${minutes}`;
    }
    if (hours < 10) {
      hours = `0${hours}`;
    }

    return `${hours}:${minutes}`;
  }

  renderBlock(constants, timeBlock) {
    // this is to check if the duration is bigger then a time to not change the block size on hover!
    const check = moment
      .duration(
        moment(timeBlock.planned_date_time_to).diff(
          moment(timeBlock.planned_date_time_from),
        ),
      )
      .asMinutes();
    const duration = timeBlock.end - timeBlock.start;
    const top = this.calcTop(timeBlock.start, constants);
    const height = (duration / constants.endAdjusted) * 100;

    return (
      <Block
        dragging={timeBlock.isActive}
        zIndex={timeBlock.zIndex || 100}
        active={timeBlock.active}
        top={top}
        height={height}
        key={`timeBlock${top}${timeBlock.id}`}
        minutes={check > 50}
        type={timeBlock.type}
        data-cy="Blocks"
      >
        {timeBlock.children && timeBlock.children}
      </Block>
    );
  }

  renderDaypart(constants, timeBlock) {
    // this is to check if the duration is bigger then a time to not change the block size on hover!
    const check = moment
      .duration(
        moment(timeBlock.planned_date_time_to).diff(
          moment(timeBlock.planned_date_time_from),
        ),
      )
      .asMinutes();
    const duration = timeBlock.end - timeBlock.start;
    const top = this.calcTop(timeBlock.start, constants);
    const height = (duration / constants.endAdjusted) * 100;

    return (
      <Daypart
        zIndex={timeBlock.zIndex || 100}
        active={timeBlock.active}
        top={top}
        height={height}
        key={`daypart${top}${timeBlock.id}`}
        minutes={check > 50}
        data-cy="Blocks"
      >
        {timeBlock.children && timeBlock.children}
      </Daypart>
    );
  }

  render() {
    const constants = this.calcConstants();
    return (
      <Wrapper
        labelOffset={this.props.labelOffset}
        width={this.props.width}
        height={this.props.height}
        data-cy="calendarWrapper"
      >
        {this.props.dayparts.map(x => this.renderDaypart(constants, x))}
        {this.props.timeBlocks.map(timeBlock =>
          this.renderBlock(constants, timeBlock),
        )}
        {this.renderHourLines(constants)}
        {this.renderHighlights(constants)}
      </Wrapper>
    );
  }
}

// first child is the timeblock Wrapper around the blocks, we do not want it also to be able to hover.
const Block = styled.div`
  z-index: ${({ zIndex }) => zIndex};
  top: ${({ top }) => top}%;
  height: ${({ height }) => height}%;
  min-height: ${({ height }) => height}%;
  width: calc(100% - 1rem);
  left: 0.5rem;
  position: absolute;
  background-color: white;

  ${({ active }) => {
    if (!active) {
      return `
        &:first-child {
          // pointer-events:none;
        }
      `;
    }

    return "";
  }};

  ${({ minutes, dragging, type }) => {
    if (!minutes && !dragging) {
      return `
          &:hover {
            ${type !== TYPES.BUTTON && "height: auto"};
            z-index: 10000;
          }
        `;
    }
  }};
`;

const Daypart = styled.div`
  z-index: ${({ zIndex }) => zIndex};
  top: ${({ top }) => top}%;
  height: ${({ height }) => height}%;
  min-height: ${({ height }) => height}%;
  width: calc(100% - 1rem);
  left: 0.5rem;
  position: absolute;
  background-color: #eee;
`;

function timeBlockStyles(zIndex, top, height) {
  return {
    zIndex,
    top: `${top}%`,
    height: `${height}%`,
    width: "calc(100% - 1rem)",
    left: "0.5rem",
    position: "absolute",
    backgroundColor: "white",
    "&:hover": { zIndex: "101", height: "auto" },
  };
}
const Wrapper = styled.div`
  //background-color: #fafafa;
  position: absolute;
  margin-left: ${({ labelOffset }) => labelOffset}px;
  width: ${({ labelOffset }) => `calc(100% - ${labelOffset}px)`};
  height: ${({ height }) => height};
`;

const Label = styled.div`
  position: absolute;
  left: -${({ labelOffset }) => labelOffset}px;
  top: -20px;
  font-variant-numeric: tabular-nums;
  // Use a different font, because Jura doesn't allow tabular-nums
  font-family: Roboto, sans-serif !important;
  font-weight: 300;
  font-size: 0.9rem;
`;

const HourLine = styled.div`
  position: absolute;
  top: ${({ top }) => top}%;
  width: 100%;
  z-index: 10;
  border-top: 1px #d8d8d8 solid;
`;
